import React, { useMemo, useState } from "react";
import { ChevronUp, ChevronDown, ChevronsUpDown, ChevronLeft, ChevronRight } from "lucide-react";
import EmptyState from "./EmptyState";

/**
 * columns: [{ key, header, render?(row), sortable?, className? }]
 * data: array of rows
 * pageSize: default 10
 */
export function DataTable({
    columns = [],
    data = [],
    pageSize = 10,
    emptyTitle = "Nessun dato",
    emptyDescription = "",
    testId = "data-table",
    getRowKey,
}) {
    const [sortKey, setSortKey] = useState(null);
    const [sortDir, setSortDir] = useState("asc");
    const [page, setPage] = useState(1);

    const sorted = useMemo(() => {
        if (!sortKey) return data;
        const copy = [...data];
        copy.sort((a, b) => {
            const av = a?.[sortKey];
            const bv = b?.[sortKey];
            if (av == null && bv == null) return 0;
            if (av == null) return 1;
            if (bv == null) return -1;
            if (typeof av === "number" && typeof bv === "number") {
                return sortDir === "asc" ? av - bv : bv - av;
            }
            return sortDir === "asc"
                ? String(av).localeCompare(String(bv))
                : String(bv).localeCompare(String(av));
        });
        return copy;
    }, [data, sortKey, sortDir]);

    const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
    const currentPage = Math.min(page, totalPages);
    const paged = sorted.slice((currentPage - 1) * pageSize, currentPage * pageSize);

    const toggleSort = (col) => {
        if (!col.sortable) return;
        if (sortKey === col.key) {
            setSortDir(sortDir === "asc" ? "desc" : "asc");
        } else {
            setSortKey(col.key);
            setSortDir("asc");
        }
    };

    if (!data || data.length === 0) {
        return <EmptyState title={emptyTitle} description={emptyDescription} />;
    }

    return (
        <div data-testid={testId} className="bg-white border border-[#DCE3EE] rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="bg-[#F5F7FB] border-b border-[#DCE3EE]">
                            {columns.map((col) => (
                                <th
                                    key={col.key}
                                    onClick={() => toggleSort(col)}
                                    className={`px-4 py-3 text-left text-[11px] font-semibold text-[#64748B] uppercase tracking-wider ${col.sortable ? "cursor-pointer select-none hover:text-[#172033]" : ""
                                        } ${col.className || ""}`}
                                >
                                    <div className="flex items-center gap-1.5">
                                        <span>{col.header}</span>
                                        {col.sortable && (
                                            sortKey === col.key ? (
                                                sortDir === "asc" ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />
                                            ) : (
                                                <ChevronsUpDown className="w-3.5 h-3.5 opacity-40" />
                                            )
                                        )}
                                    </div>
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {paged.map((row, i) => {
                            const key = getRowKey ? getRowKey(row) : row.id || i;
                            return (
                                <tr
                                    key={key}
                                    className="border-b border-[#DCE3EE] last:border-0 hover:bg-[#F5F7FB]/60 transition-colors"
                                >
                                    {columns.map((col) => (
                                        <td key={col.key} className={`px-4 py-3 text-[#172033] ${col.className || ""}`}>
                                            {col.render ? col.render(row) : row?.[col.key] ?? "—"}
                                        </td>
                                    ))}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
            {totalPages > 1 && (
                <div className="flex items-center justify-between px-4 py-3 border-t border-[#DCE3EE] bg-white">
                    <div className="text-xs text-[#64748B]">
                        Pagina <span className="font-semibold text-[#172033]">{currentPage}</span> di{" "}
                        <span className="font-semibold text-[#172033]">{totalPages}</span> ·{" "}
                        {sorted.length} {sorted.length === 1 ? "elemento" : "elementi"}
                    </div>
                    <div className="flex items-center gap-1">
                        <button
                            data-testid="datatable-prev-btn"
                            className="p-1.5 rounded-md border border-[#DCE3EE] hover:bg-[#F5F7FB] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                            onClick={() => setPage((p) => Math.max(1, p - 1))}
                            disabled={currentPage <= 1}
                        >
                            <ChevronLeft className="w-4 h-4 text-[#64748B]" />
                        </button>
                        <button
                            data-testid="datatable-next-btn"
                            className="p-1.5 rounded-md border border-[#DCE3EE] hover:bg-[#F5F7FB] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                            disabled={currentPage >= totalPages}
                        >
                            <ChevronRight className="w-4 h-4 text-[#64748B]" />
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

export default DataTable;
